import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  Clock,
  Image,
  Video,
  X,
  Check,
  AlertTriangle,
  Calendar,
  Sparkles,
  Trash2,
  Hash,
  AlertCircle,
  Info,
} from 'lucide-react';
import { api, type SMCCMediaItem } from '../../../services/api';
import {
  PLATFORM_CONFIG,
  type SMCCPlatformId,
} from '../../../constants/smcc';
import {
  validateContentForPlatforms,
  isContentValidForAllPlatforms,
  getAllValidationErrors,
  type ContentValidation,
} from '../../../utils/smcc';
import { useLanguage } from '../../../contexts/LanguageContext';

// Content types - keep local with Lucide component references
const getContentTypes = (t: (key: string, fallback: string) => string) => [
  { value: 'post', label: t('smccContentEditor.contentTypes.post', 'Post'), icon: Image },
  { value: 'story', label: t('smccContentEditor.contentTypes.story', 'Story'), icon: Clock },
  { value: 'reel', label: t('smccContentEditor.contentTypes.reel', 'Reel'), icon: Video },
  { value: 'video', label: t('smccContentEditor.contentTypes.video', 'Video'), icon: Video },
  { value: 'carousel', label: t('smccContentEditor.contentTypes.carousel', 'Carousel'), icon: Image },
];

// Automation levels - keep local for type safety
const getAutomationLevels = (t: (key: string, fallback: string) => string) => [
  { value: 'manual', label: t('smccContentEditor.automationLevels.manual.label', 'Manual'), description: t('smccContentEditor.automationLevels.manual.description', 'Publish manually when ready') },
  { value: 'human_approval', label: t('smccContentEditor.automationLevels.humanApproval.label', 'Requires Approval'), description: t('smccContentEditor.automationLevels.humanApproval.description', 'Needs human approval before publishing') },
  { value: 'fully_automated', label: t('smccContentEditor.automationLevels.fullyAutomated.label', 'Automated'), description: t('smccContentEditor.automationLevels.fullyAutomated.description', 'Publish automatically when scheduled') },
];

interface ContentFormData {
  title: string;
  body: string;
  content_type: string;
  target_platforms: SMCCPlatformId[];
  media_urls: SMCCMediaItem[];
  automation_level: string;
  scheduled_at: string;
  hashtags: string[];
  link_url: string;
}

function PlatformPreview({
  platform,
  body,
  hashtags,
  mediaUrls,
  validation,
}: {
  platform: SMCCPlatformId;
  body: string;
  hashtags: string[];
  mediaUrls: SMCCMediaItem[];
  validation?: ContentValidation;
}) {
  const config = PLATFORM_CONFIG[platform];
  if (!config) return null;

  const fullText = hashtags.length > 0 ? `${body}\n\n${hashtags.map((h) => `#${h}`).join(' ')}` : body;
  const hasErrors = validation && !validation.isValid;
  const hasWarnings = validation && validation.warnings.length > 0;
  const charPercent = Math.min(100, (fullText.length / config.maxLength) * 100);

  return (
    <div className={`bg-white rounded-lg border overflow-hidden ${hasErrors ? 'border-red-300' : 'border-gray-200'}`}>
      {/* Platform Header */}
      <div
        className="px-3 py-2 flex items-center gap-2"
        style={{ backgroundColor: hasErrors ? '#FEF2F2' : `${config.color}15` }}
      >
        <div
          className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
          style={{ backgroundColor: config.color }}
        >
          {config.name[0]}
        </div>
        <span className="font-medium text-gray-900 text-sm">{config.name}</span>
        {hasErrors && <AlertCircle className="w-4 h-4 text-red-500 ml-auto" />}
        {!hasErrors && hasWarnings && <AlertTriangle className="w-4 h-4 text-amber-500 ml-auto" />}
        {!hasErrors && !hasWarnings && validation && <Check className="w-4 h-4 text-green-500 ml-auto" />}
      </div>

      {/* Preview Content */}
      <div className="p-3">
        {/* Validation Errors */}
        {hasErrors && (
          <div className="mb-3 p-2 bg-red-50 rounded-lg text-xs text-red-600 space-y-1">
            {validation.errors.map((error, i) => (
              <div key={i} className="flex items-start gap-1">
                <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            ))}
          </div>
        )}

        {/* Validation Warnings */}
        {!hasErrors && hasWarnings && (
          <div className="mb-3 p-2 bg-amber-50 rounded-lg text-xs text-amber-600 space-y-1">
            {validation!.warnings.map((warning, i) => (
              <div key={i} className="flex items-start gap-1">
                <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                <span>{warning}</span>
              </div>
            ))}
          </div>
        )}

        {/* Media Preview */}
        {mediaUrls.length > 0 && (
          <div className="mb-3 bg-gray-100 rounded-lg aspect-square max-w-[200px] flex items-center justify-center">
            {mediaUrls[0].type === 'video' ? (
              <Video className="w-8 h-8 text-gray-400" />
            ) : (
              <img
                src={mediaUrls[0].url}
                alt="Preview"
                className="w-full h-full object-cover rounded-lg"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            )}
          </div>
        )}

        {/* Text Preview */}
        <div className="text-sm text-gray-700 whitespace-pre-wrap break-words">
          {body.slice(0, 200)}
          {body.length > 200 && '...'}
        </div>

        {/* Hashtags */}
        {hashtags.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {hashtags.slice(0, 5).map((tag, i) => (
              <span key={i} className="text-xs text-blue-600">
                #{tag}
              </span>
            ))}
            {hashtags.length > 5 && (
              <span className="text-xs text-gray-400">+{hashtags.length - 5} more</span>
            )}
          </div>
        )}

        {/* Character Count Progress Bar */}
        <div className="mt-3 pt-2 border-t border-gray-100 space-y-2">
          <div className="relative h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={`absolute left-0 top-0 h-full rounded-full transition-all ${
                charPercent > 100 ? 'bg-red-500' : charPercent > 90 ? 'bg-amber-500' : 'bg-green-500'
              }`}
              style={{ width: `${Math.min(100, charPercent)}%` }}
            />
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className={charPercent > 100 ? 'text-red-600 font-medium' : 'text-gray-400'}>
              {fullText.length.toLocaleString()} / {config.maxLength.toLocaleString()} chars
            </span>
            {config.maxHashtags > 0 && (
              <span className={hashtags.length > config.maxHashtags ? 'text-red-600 font-medium' : 'text-gray-400'}>
                {hashtags.length} / {config.maxHashtags} tags
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SMCCContentEditor() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isNew = !id || id === 'new';
  const { t } = useLanguage();
  const CONTENT_TYPES = getContentTypes(t);
  const AUTOMATION_LEVELS = getAutomationLevels(t);

  const [formData, setFormData] = useState<ContentFormData>({
    title: '',
    body: '',
    content_type: 'post',
    target_platforms: ['instagram'] as SMCCPlatformId[],
    media_urls: [],
    automation_level: 'manual',
    scheduled_at: searchParams.get('scheduled_at') || '',
    hashtags: [],
    link_url: '',
  });

  const [hashtagInput, setHashtagInput] = useState('');
  const [mediaUrlInput, setMediaUrlInput] = useState('');
  const [showScheduler, setShowScheduler] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiIncludeHashtags, setAiIncludeHashtags] = useState(true);
  const [aiIncludeEmojis, setAiIncludeEmojis] = useState(true);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Real-time validation for all selected platforms
  const validationResults = useMemo(() => {
    return validateContentForPlatforms(
      formData.target_platforms,
      formData.body,
      formData.hashtags,
      formData.media_urls.length
    );
  }, [formData.target_platforms, formData.body, formData.hashtags, formData.media_urls.length]);

  const isFormValid = useMemo(() => {
    if (formData.target_platforms.length === 0) return false;
    if (!formData.body.trim()) return false;
    return isContentValidForAllPlatforms(validationResults);
  }, [formData.target_platforms, formData.body, validationResults]);

  const allValidationErrors = useMemo(() => {
    return getAllValidationErrors(validationResults);
  }, [validationResults]);

  // Fetch existing content if editing
  const { data: contentData, isLoading: loadingContent } = useQuery({
    queryKey: ['smcc-content', id],
    queryFn: () => api.getSMCCContentById(id!),
    enabled: !isNew && !!id,
  });

  // Populate form when content loads
  useEffect(() => {
    if (contentData?.content) {
      const content = contentData.content;
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Initialize form with fetched data
      setFormData({
        title: content.title || '',
        body: content.body || '',
        content_type: content.content_type || 'post',
        target_platforms: (content.target_platforms || ['instagram']) as SMCCPlatformId[],
        media_urls: content.media_urls || [],
        automation_level: content.automation_level || 'manual',
        scheduled_at: content.scheduled_at || '',
        hashtags: [], // Extract from body if needed
        link_url: '',
      });
    }
  }, [contentData]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: typeof formData) => api.createSMCCContent(data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['smcc-content'] });
      showSuccess('Content created successfully');
      setTimeout(() => navigate(`/admin/smcc/content/${result.content.id}`), 1000);
    },
    onError: (error: Error) => showError(error.message || 'Failed to create content'),
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: Partial<typeof formData>) => api.updateSMCCContent(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['smcc-content', id] });
      showSuccess('Content saved successfully');
    },
    onError: (error: Error) => showError(error.message || 'Failed to save content'),
  });

  // Schedule mutation
  const scheduleMutation = useMutation({
    mutationFn: (scheduledAt: string) => api.scheduleSMCCContent(id!, scheduledAt),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['smcc-content', id] });
      showSuccess('Content scheduled successfully');
      setShowScheduler(false);
    },
    onError: (error: Error) => showError(error.message || 'Failed to schedule content'),
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: () => api.deleteSMCCContent(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['smcc-content'] });
      showSuccess('Content deleted');
      setTimeout(() => navigate('/admin/smcc/calendar'), 1000);
    },
    onError: (error: Error) => showError(error.message || 'Failed to delete content'),
  });

  // AI content generation mutation
  const aiGenerateMutation = useMutation({
    mutationFn: (params: {
      prompt: string;
      contentType: string;
      targetPlatforms: string[];
      includeHashtags?: boolean;
      includeEmojis?: boolean;
    }) => api.generateSMCCContent(params),
    onSuccess: (result) => {
      // Update form with generated content
      setFormData((prev) => ({
        ...prev,
        body: result.body,
        hashtags: result.hashtags || [],
      }));
      setShowAIModal(false);
      setAiPrompt('');
      showSuccess('Content generated with AI');
    },
    onError: (error: Error) => showError(error.message || 'Failed to generate content'),
  });

  const handleAIGenerate = () => {
    if (!aiPrompt.trim()) {
      showError('Please enter a prompt for AI generation');
      return;
    }
    if (formData.target_platforms.length === 0) {
      showError('Please select at least one target platform');
      return;
    }
    aiGenerateMutation.mutate({
      prompt: aiPrompt,
      contentType: formData.content_type,
      targetPlatforms: formData.target_platforms,
      includeHashtags: aiIncludeHashtags,
      includeEmojis: aiIncludeEmojis,
    });
  };

  const showSuccess = (msg: string) => {
    setSuccessMessage(msg);
    setErrorMessage('');
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const showError = (msg: string) => {
    setErrorMessage(msg);
    setSuccessMessage('');
    setTimeout(() => setErrorMessage(''), 5000);
  };

  const handleSave = () => {
    // Validate content before saving
    if (formData.target_platforms.length === 0) {
      showError('Please select at least one platform');
      return;
    }

    if (!formData.body.trim()) {
      showError('Content body is required');
      return;
    }

    // Check for validation errors
    if (allValidationErrors.length > 0) {
      showError(`Fix validation errors before saving: ${allValidationErrors[0]}`);
      return;
    }

    // Build body with hashtags
    const bodyWithHashtags =
      formData.hashtags.length > 0
        ? `${formData.body}\n\n${formData.hashtags.map((h) => `#${h}`).join(' ')}`
        : formData.body;

    const data = {
      title: formData.title,
      body: bodyWithHashtags,
      content_type: formData.content_type,
      target_platforms: formData.target_platforms,
      media_urls: formData.media_urls,
      automation_level: formData.automation_level,
      scheduled_at: formData.scheduled_at || '',
      hashtags: formData.hashtags,
      link_url: formData.link_url,
    };

    if (isNew) {
      createMutation.mutate(data);
    } else {
      updateMutation.mutate(data);
    }
  };

  const handleAddHashtag = () => {
    const tag = hashtagInput.trim().replace(/^#/, '');
    if (tag && !formData.hashtags.includes(tag)) {
      setFormData((prev) => ({ ...prev, hashtags: [...prev.hashtags, tag] }));
      setHashtagInput('');
    }
  };

  const handleRemoveHashtag = (tag: string) => {
    setFormData((prev) => ({
      ...prev,
      hashtags: prev.hashtags.filter((h) => h !== tag),
    }));
  };

  const handleAddMedia = () => {
    const url = mediaUrlInput.trim();
    if (url) {
      const type = url.match(/\.(mp4|mov|avi|webm)$/i) ? 'video' : 'image';
      setFormData((prev) => ({
        ...prev,
        media_urls: [...prev.media_urls, { url, type }],
      }));
      setMediaUrlInput('');
    }
  };

  const handleRemoveMedia = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      media_urls: prev.media_urls.filter((_, i) => i !== index),
    }));
  };

  const togglePlatform = (platform: SMCCPlatformId) => {
    setFormData((prev) => ({
      ...prev,
      target_platforms: prev.target_platforms.includes(platform)
        ? prev.target_platforms.filter((p) => p !== platform)
        : [...prev.target_platforms, platform],
    }));
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  if (!isNew && loadingContent) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" role="main" aria-label="Content Editor">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            aria-label="Go back"
          >
            <ArrowLeft className="w-5 h-5" aria-hidden="true" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900" id="content-editor-title">
              {isNew ? t('smccContentEditor.createContent', 'Create Content') : t('smccContentEditor.editContent', 'Edit Content')}
            </h1>
            <p className="text-gray-500 mt-0.5">
              {isNew ? t('smccContentEditor.createSubtitle', 'Create new social media content') : t('smccContentEditor.editSubtitle', 'Edit and manage your content')}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2" role="toolbar" aria-label="Content actions">
          {!isNew && (
            <button
              onClick={() => {
                if (confirm('Delete this content?')) {
                  deleteMutation.mutate();
                }
              }}
              className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg flex items-center gap-2 focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              aria-label="Delete content"
            >
              <Trash2 className="w-4 h-4" aria-hidden="true" />
              <span>{t('smccContentEditor.delete', 'Delete')}</span>
            </button>
          )}
          <button
            onClick={() => setShowScheduler(true)}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            aria-label="Schedule content"
          >
            <Clock className="w-4 h-4" aria-hidden="true" />
            <span>{t('smccContentEditor.schedule', 'Schedule')}</span>
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || !isFormValid}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
              isFormValid
                ? 'bg-primary-600 text-white hover:bg-primary-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            } disabled:opacity-50`}
            title={!isFormValid ? t('smccContentEditor.fixValidationErrors', 'Fix validation errors to save') : undefined}
          >
            <Save className="w-4 h-4" />
            {isSaving ? t('smccContentEditor.saving', 'Saving...') : t('smccContentEditor.saveDraft', 'Save Draft')}
          </button>
        </div>
      </div>

      {/* Messages */}
      {successMessage && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 flex items-center gap-2" role="alert">
          <Check className="w-5 h-5" />
          {successMessage}
        </div>
      )}
      {errorMessage && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center gap-2" role="alert">
          <AlertTriangle className="w-5 h-5" />
          {errorMessage}
        </div>
      )}

      {/* Validation Summary - show when there are errors and no other message is showing */}
      {!successMessage && !errorMessage && allValidationErrors.length > 0 && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">Content has validation issues</p>
              <ul className="mt-1 text-sm text-amber-700 list-disc list-inside">
                {allValidationErrors.slice(0, 3).map((error, i) => (
                  <li key={i}>{error}</li>
                ))}
                {allValidationErrors.length > 3 && (
                  <li>...and {allValidationErrors.length - 3} more issues</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-3 gap-6">
        {/* Editor Column */}
        <div className="col-span-2 space-y-6">
          {/* Title */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <label htmlFor="content-title" className="block text-sm font-medium text-gray-700 mb-2">Title (optional)</label>
            <input
              id="content-title"
              type="text"
              value={formData.title}
              onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
              placeholder="Internal title for this content"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Content Type */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <span className="block text-sm font-medium text-gray-700 mb-3">Content Type</span>
            <div className="flex flex-wrap gap-2">
              {CONTENT_TYPES.map((type) => (
                <button
                  key={type.value}
                  onClick={() => setFormData((prev) => ({ ...prev, content_type: type.value }))}
                  className={`px-4 py-2 rounded-lg border flex items-center gap-2 transition-colors ${
                    formData.content_type === type.value
                      ? 'bg-primary-50 border-primary-300 text-primary-700'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <type.icon className="w-4 h-4" />
                  {type.label}
                </button>
              ))}
            </div>
          </div>

          {/* Body */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <label htmlFor="content-body" className="block text-sm font-medium text-gray-700 mb-2">Content Body</label>
            <textarea
              id="content-body"
              value={formData.body}
              onChange={(e) => setFormData((prev) => ({ ...prev, body: e.target.value }))}
              placeholder="Write your post content here..."
              rows={8}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
            />
            <div className="mt-2 flex items-center justify-between text-xs text-gray-400">
              <span>{formData.body.length} characters</span>
              <button
                type="button"
                onClick={() => setShowAIModal(true)}
                className="flex items-center gap-1 text-primary-600 hover:text-primary-700"
              >
                <Sparkles className="w-3 h-3" />
                Generate with AI
              </button>
            </div>
          </div>

          {/* Hashtags */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <label htmlFor="hashtag-input" className="block text-sm font-medium text-gray-700 mb-2">
              <Hash className="w-4 h-4 inline mr-1" />
              Hashtags
            </label>
            <div className="flex gap-2 mb-3">
              <input
                id="hashtag-input"
                type="text"
                value={hashtagInput}
                onChange={(e) => setHashtagInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddHashtag())}
                placeholder="Add hashtag"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <button
                onClick={handleAddHashtag}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Add
              </button>
            </div>
            {formData.hashtags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.hashtags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-sm"
                  >
                    #{tag}
                    <button
                      onClick={() => handleRemoveHashtag(tag)}
                      className="hover:text-blue-900"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Media */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <label htmlFor="media-url-input" className="block text-sm font-medium text-gray-700 mb-2">
              <Image className="w-4 h-4 inline mr-1" />
              Media
            </label>
            <div className="flex gap-2 mb-3">
              <input
                id="media-url-input"
                type="text"
                value={mediaUrlInput}
                onChange={(e) => setMediaUrlInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddMedia())}
                placeholder="Enter image or video URL"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <button
                onClick={handleAddMedia}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Add
              </button>
            </div>
            {formData.media_urls.length > 0 && (
              <div className="grid grid-cols-4 gap-2">
                {formData.media_urls.map((media, index) => (
                  <div key={index} className="relative group">
                    <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                      {media.type === 'video' ? (
                        <div className="w-full h-full flex items-center justify-center">
                          <Video className="w-8 h-8 text-gray-400" />
                        </div>
                      ) : (
                        <img
                          src={media.url}
                          alt={`Media ${index + 1}`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = '';
                            (e.target as HTMLImageElement).className = 'hidden';
                          }}
                        />
                      )}
                    </div>
                    <button
                      onClick={() => handleRemoveMedia(index)}
                      className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Automation Level */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <span className="block text-sm font-medium text-gray-700 mb-3">Automation Level</span>
            <div className="space-y-2">
              {AUTOMATION_LEVELS.map((level) => (
                <label
                  key={level.value}
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    formData.automation_level === level.value
                      ? 'bg-primary-50 border-primary-300'
                      : 'bg-white border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="automation_level"
                    value={level.value}
                    checked={formData.automation_level === level.value}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, automation_level: e.target.value }))
                    }
                    className="mt-1"
                  />
                  <div>
                    <p className="font-medium text-gray-900">{level.label}</p>
                    <p className="text-sm text-gray-500">{level.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Target Platforms */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <span className="block text-sm font-medium text-gray-700 mb-3">Target Platforms</span>
            <div className="space-y-2">
              {(Object.keys(PLATFORM_CONFIG) as SMCCPlatformId[]).map((platform) => {
                const config = PLATFORM_CONFIG[platform];
                const platformValidation = validationResults.get(platform);
                const hasError = platformValidation && !platformValidation.isValid;
                const isSelected = formData.target_platforms.includes(platform);

                return (
                  <label
                    key={platform}
                    className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                      isSelected
                        ? hasError
                          ? 'bg-red-50 border border-red-200'
                          : 'bg-gray-100'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => togglePlatform(platform)}
                      className="rounded"
                    />
                    <div
                      className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
                      style={{ backgroundColor: config.color }}
                    >
                      {config.name[0]}
                    </div>
                    <span className="text-sm text-gray-700 flex-1">{config.name}</span>
                    {isSelected && hasError && (
                      <AlertCircle className="w-4 h-4 text-red-500" />
                    )}
                    {isSelected && !hasError && platformValidation && (
                      <Check className="w-4 h-4 text-green-500" />
                    )}
                  </label>
                );
              })}
            </div>
            {formData.target_platforms.length === 0 && (
              <p className="mt-2 text-xs text-red-500">Select at least one platform</p>
            )}
          </div>

          {/* Platform Previews */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-700">Platform Previews</h3>
              {isFormValid && formData.target_platforms.length > 0 && (
                <span className="text-xs text-green-600 flex items-center gap-1">
                  <Check className="w-3 h-3" />
                  All platforms valid
                </span>
              )}
            </div>
            {formData.target_platforms.map((platform) => (
              <PlatformPreview
                key={platform}
                platform={platform}
                body={formData.body}
                hashtags={formData.hashtags}
                mediaUrls={formData.media_urls}
                validation={validationResults.get(platform)}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Schedule Modal */}
      {showScheduler && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setShowScheduler(false)}
            onKeyDown={(e) => e.key === 'Escape' && setShowScheduler(false)}
            role="button"
            tabIndex={0}
            aria-label="Close modal"
          />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-xl z-50 w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Schedule Content</h3>

            <div className="space-y-4">
              <div>
                <label htmlFor="schedule-datetime" className="block text-sm font-medium text-gray-700 mb-2">
                  Date and Time
                </label>
                <input
                  id="schedule-datetime"
                  type="datetime-local"
                  value={formData.scheduled_at.slice(0, 16)}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      scheduled_at: new Date(e.target.value).toISOString(),
                    }))
                  }
                  min={new Date().toISOString().slice(0, 16)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowScheduler(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  {t('smccContentEditor.cancel', 'Cancel')}
                </button>
                <button
                  onClick={() => {
                    if (formData.scheduled_at) {
                      if (isNew) {
                        handleSave();
                      } else {
                        scheduleMutation.mutate(formData.scheduled_at);
                      }
                    }
                  }}
                  disabled={!formData.scheduled_at || scheduleMutation.isPending}
                  className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <Calendar className="w-4 h-4" />
                  {scheduleMutation.isPending ? 'Scheduling...' : 'Schedule'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* AI Generation Modal */}
      {showAIModal && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setShowAIModal(false)}
            onKeyDown={(e) => e.key === 'Escape' && setShowAIModal(false)}
            role="button"
            tabIndex={0}
            aria-label="Close modal"
          />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-xl z-50 w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Generate with AI</h3>
                  <p className="text-sm text-gray-500">Create content using AI</p>
                </div>
              </div>
              <button
                onClick={() => setShowAIModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Prompt Input */}
              <div>
                <label htmlFor="ai-prompt-input" className="block text-sm font-medium text-gray-700 mb-2">
                  What would you like to post about?
                </label>
                <textarea
                  id="ai-prompt-input"
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  placeholder="Describe your post idea... e.g., 'A post about our new product launch with an exciting announcement'"
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                />
              </div>

              {/* Options */}
              <div className="flex items-center gap-4">
                <label htmlFor="ai-include-hashtags" className="flex items-center gap-2 cursor-pointer">
                  <input
                    id="ai-include-hashtags"
                    type="checkbox"
                    checked={aiIncludeHashtags}
                    onChange={(e) => setAiIncludeHashtags(e.target.checked)}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">Include hashtags</span>
                </label>
                <label htmlFor="ai-include-emojis" className="flex items-center gap-2 cursor-pointer">
                  <input
                    id="ai-include-emojis"
                    type="checkbox"
                    checked={aiIncludeEmojis}
                    onChange={(e) => setAiIncludeEmojis(e.target.checked)}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">Include emojis</span>
                </label>
              </div>

              {/* Target Platforms Info */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 mb-2">Generating for:</p>
                <div className="flex flex-wrap gap-1">
                  {formData.target_platforms.map((platform) => {
                    const config = PLATFORM_CONFIG[platform];
                    return (
                      <span
                        key={platform}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-white"
                        style={{ backgroundColor: config?.color || '#666' }}
                      >
                        {config?.name || platform}
                      </span>
                    );
                  })}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowAIModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  {t('smccContentEditor.cancel', 'Cancel')}
                </button>
                <button
                  onClick={handleAIGenerate}
                  disabled={!aiPrompt.trim() || aiGenerateMutation.isPending}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {aiGenerateMutation.isPending ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      Generate
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
